import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Peer, { MediaConnection } from "peerjs";
import { getIceServers, setSessionPeerId } from "../services/auth";
import Config from "../Config";

const RETRY_DELAY_MS = 3000;

type CallMetadata = { type: "camera" | "screen" };

export type RemoteStreamEntry = {
  id: string;
  peerId: string;
  stream: MediaStream;
  isScreen: boolean;
};

type SessionHelper = {
  session: { sessionId: string };
};

export type StartSessionOptions = {
  sessionId: string;
  userOrStudentId: string;
  bearer: string;
  // Students only: returns the current peer id of the teacher, who is called
  // (again) whenever there is no connection with them
  getRemotePeerId?: () => Promise<string | null>;
};

export const OTSessionContext = createContext<{
  start: (options: StartSessionOptions) => void;
  leave: () => void;
  sessionHelper: SessionHelper | null;
  connected: boolean;
  localStream: MediaStream | null;
  remoteStreams: RemoteStreamEntry[];
  audioEnabled: boolean;
  videoEnabled: boolean;
  setAudioEnabled: (value: boolean) => void;
  setVideoEnabled: (value: boolean) => void;
  screenStream: MediaStream | null;
  startScreenShare: () => Promise<boolean>;
  stopScreenShare: () => void;
} | null>(null);

export function ProvideOTSession({ children }: { children: React.ReactNode }) {
  const provider = useProvideOTSession();
  return (
    <OTSessionContext.Provider value={provider}>
      {children}
    </OTSessionContext.Provider>
  );
}

export const useOTSession = () => {
  const context = useContext(OTSessionContext);
  if (context === null) {
    throw Error("OTSession context not provided");
  }
  return context;
};

// Connects to our own PeerJS signaling server, relaying media through our TURN
// server when a direct connection is not possible
const createPeer = async (
  sessionId: string,
  userOrStudentId: string,
  bearer: string
) => {
  const iceServers = await getIceServers(sessionId, userOrStudentId, bearer);
  return new Peer({
    ...Config.PEER,
    config: {
      iceServers:
        !iceServers.error && Array.isArray(iceServers.data)
          ? iceServers.data
          : [{ urls: "stun:stun.l.google.com:19302" }],
    },
  });
};

// Black video / silent audio, sent in place of a camera or microphone the user
// does not have or refused, so that the other side still receives our media
const createPlaceholderTrack = (kind: "audio" | "video") => {
  if (kind === "video") {
    const canvas = Object.assign(document.createElement("canvas"), {
      width: 640,
      height: 480,
    });
    canvas.getContext("2d")?.fillRect(0, 0, canvas.width, canvas.height);
    return canvas.captureStream(1).getVideoTracks()[0];
  }
  const audioContext = new AudioContext();
  return audioContext.createMediaStreamDestination().stream.getAudioTracks()[0];
};

const getLocalMedia = async () => {
  const constraintsToTry = [
    { video: true, audio: true },
    { audio: true },
    { video: true },
  ];

  let stream = new MediaStream();
  for (const constraints of constraintsToTry) {
    try {
      stream = await navigator.mediaDevices.getUserMedia(constraints);
      break;
    } catch (e) {
      console.warn("[WebRTC] getUserMedia failed", constraints, e);
    }
  }

  if (stream.getVideoTracks().length === 0) {
    stream.addTrack(createPlaceholderTrack("video"));
  }
  if (stream.getAudioTracks().length === 0) {
    stream.addTrack(createPlaceholderTrack("audio"));
  }
  return stream;
};

const stopStream = (stream: MediaStream | null) => {
  stream?.getTracks().forEach((track) => track.stop());
};

const useProvideOTSession = () => {
  const peerRef = useRef<Peer | null>(null);
  const optionsRef = useRef<StartSessionOptions | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  // Camera calls (both directions) and outgoing screen calls, by remote peer id
  const cameraCalls = useRef<Map<string, MediaConnection>>(new Map());
  const screenCalls = useRef<Map<string, MediaConnection>>(new Map());
  const retryTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callRemoteRef = useRef<() => void>(() => {});
  const left = useRef(false);

  const [sessionHelper, setSessionHelper] = useState<SessionHelper | null>(
    null
  );
  const [connected, setConnected] = useState(false);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [remoteStreams, setRemoteStreams] = useState<RemoteStreamEntry[]>([]);
  // Like with OpenTok, users join with their camera and microphone off
  const [audioEnabled, setAudioEnabledState] = useState(false);
  const [videoEnabled, setVideoEnabledState] = useState(false);

  const setAudioEnabled = useCallback((value: boolean) => {
    localStreamRef.current
      ?.getAudioTracks()
      .forEach((track) => (track.enabled = value));
    setAudioEnabledState(value);
  }, []);

  const setVideoEnabled = useCallback((value: boolean) => {
    localStreamRef.current
      ?.getVideoTracks()
      .forEach((track) => (track.enabled = value));
    setVideoEnabledState(value);
  }, []);

  const removeRemoteStream = useCallback((id: string) => {
    setRemoteStreams((streams) => streams.filter((s) => s.id !== id));
  }, []);

  // Shows the media received on a call, until the call is closed
  const trackCall = useCallback(
    (call: MediaConnection, isScreen: boolean, onClose: () => void) => {
      // "stream" is emitted once per track, with the same stream
      call.on("stream", (stream) => {
        setRemoteStreams((streams) => [
          ...streams.filter((s) => s.id !== call.connectionId),
          { id: call.connectionId, peerId: call.peer, stream, isScreen },
        ]);
      });

      let closed = false;
      const handleClose = () => {
        if (closed) {
          return;
        }
        closed = true;
        removeRemoteStream(call.connectionId);
        onClose();
      };
      call.on("close", handleClose);
      call.on("error", (e) => {
        console.warn("[WebRTC] Call error", e);
        call.close();
        handleClose();
      });
    },
    [removeRemoteStream]
  );

  const callScreen = useCallback(
    (remotePeerId: string) => {
      const peer = peerRef.current;
      const stream = screenStreamRef.current;
      if (!peer || !stream || screenCalls.current.has(remotePeerId)) {
        return;
      }
      const metadata: CallMetadata = { type: "screen" };
      const call = peer.call(remotePeerId, stream, { metadata });
      screenCalls.current.set(remotePeerId, call);
      trackCall(call, true, () => {
        if (screenCalls.current.get(remotePeerId) === call) {
          screenCalls.current.delete(remotePeerId);
        }
      });
    },
    [trackCall]
  );

  const scheduleCallRemote = useCallback(
    (delay: number) => {
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
      retryTimeout.current = setTimeout(() => {
        retryTimeout.current = null;
        callRemoteRef.current();
      }, delay);
    },
    []
  );

  const onCameraCallStarted = useCallback(
    (call: MediaConnection, isOutgoing: boolean) => {
      cameraCalls.current.get(call.peer)?.close();
      cameraCalls.current.set(call.peer, call);

      trackCall(call, false, () => {
        if (cameraCalls.current.get(call.peer) !== call) {
          return;
        }
        cameraCalls.current.delete(call.peer);
        // The other side left: stop sending it our screen
        screenCalls.current.get(call.peer)?.close();
        screenCalls.current.delete(call.peer);
        if (isOutgoing && !left.current) {
          scheduleCallRemote(RETRY_DELAY_MS);
        }
      });

      // Somebody joining while we are sharing our screen must receive it too
      callScreen(call.peer);
    },
    [trackCall, callScreen, scheduleCallRemote]
  );

  // Students call the teacher, and call again until a connection is established
  const callRemote = useCallback(async () => {
    const peer = peerRef.current;
    const getRemotePeerId = optionsRef.current?.getRemotePeerId;
    if (!peer || peer.destroyed || !getRemotePeerId || left.current) {
      return;
    }

    const remotePeerId = await getRemotePeerId();
    if (left.current) {
      return;
    }
    if (!remotePeerId || remotePeerId === "0") {
      scheduleCallRemote(RETRY_DELAY_MS);
      return;
    }
    if (cameraCalls.current.has(remotePeerId)) {
      return;
    }

    const metadata: CallMetadata = { type: "camera" };
    const call = peer.call(remotePeerId, localStreamRef.current!, {
      metadata,
    });
    onCameraCallStarted(call, true);
  }, [onCameraCallStarted, scheduleCallRemote]);

  useEffect(() => {
    callRemoteRef.current = callRemote;
  }, [callRemote]);

  const start = useCallback(
    async (options: StartSessionOptions) => {
      if (optionsRef.current) {
        return;
      }
      optionsRef.current = options;
      const { sessionId, userOrStudentId, bearer } = options;

      setSessionHelper({ session: { sessionId } });

      const stream = await getLocalMedia();
      if (left.current) {
        stopStream(stream);
        return;
      }
      stream.getTracks().forEach((track) => (track.enabled = false));
      localStreamRef.current = stream;
      setLocalStream(stream);

      const peer = await createPeer(sessionId, userOrStudentId, bearer);
      if (left.current) {
        peer.destroy();
        return;
      }
      peerRef.current = peer;

      peer.on("open", async (peerId) => {
        await setSessionPeerId(sessionId, userOrStudentId, peerId, bearer);
        setConnected(true);
        callRemote();
      });

      peer.on("call", (call) => {
        if ((call.metadata as CallMetadata | undefined)?.type === "screen") {
          // Receive only
          call.answer();
          trackCall(call, true, () => {});
          return;
        }
        call.answer(localStreamRef.current!);
        onCameraCallStarted(call, false);
      });

      peer.on("disconnected", () => {
        // Lost the signaling server: ongoing calls are not affected
        if (!left.current && !peer.destroyed) {
          setTimeout(() => !peer.destroyed && peer.reconnect(), RETRY_DELAY_MS);
        }
      });

      peer.on("error", (e) => {
        console.warn("[WebRTC] Peer error", e.type, e);
        if (e.type === "peer-unavailable") {
          // The teacher's peer id is outdated (reload) or not registered yet
          const remotePeerId = e.message.split(" ").pop() ?? "";
          cameraCalls.current.get(remotePeerId)?.close();
          scheduleCallRemote(RETRY_DELAY_MS);
        }
      });
    },
    [callRemote, onCameraCallStarted, trackCall, scheduleCallRemote]
  );

  const stopScreenShare = useCallback(() => {
    screenCalls.current.forEach((call) => call.close());
    screenCalls.current.clear();
    stopStream(screenStreamRef.current);
    screenStreamRef.current = null;
    setScreenStream(null);
  }, []);

  const startScreenShare = useCallback(async () => {
    if (screenStreamRef.current) {
      return true;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
    } catch (e) {
      // Refused, or not supported (mobile browsers)
      console.warn("[WebRTC] getDisplayMedia failed", e);
      return false;
    }

    // Stopped from the browser's "Stop sharing" button
    stream.getVideoTracks()[0]?.addEventListener("ended", stopScreenShare);

    screenStreamRef.current = stream;
    setScreenStream(stream);
    cameraCalls.current.forEach((_, remotePeerId) => callScreen(remotePeerId));
    return true;
  }, [callScreen, stopScreenShare]);

  const leave = useCallback(() => {
    left.current = true;
    if (retryTimeout.current) {
      clearTimeout(retryTimeout.current);
    }
    stopScreenShare();
    // Closes all the calls, the other side is notified through PeerJS's data channel
    peerRef.current?.destroy();
    peerRef.current = null;
    cameraCalls.current.clear();
    stopStream(localStreamRef.current);
    localStreamRef.current = null;
    setLocalStream(null);
    setRemoteStreams([]);
    setConnected(false);
  }, [stopScreenShare]);

  useEffect(() => {
    window.addEventListener("pagehide", leave);
    return () => {
      window.removeEventListener("pagehide", leave);
    };
  }, [leave]);

  return {
    start,
    leave,
    sessionHelper,
    connected,
    localStream,
    remoteStreams,
    audioEnabled,
    videoEnabled,
    setAudioEnabled,
    setVideoEnabled,
    screenStream,
    startScreenShare,
    stopScreenShare,
  };
};
